import UAParser from '@amplitude/ua-parser-js';

class SegmentLoader {
    constructor(windowObject) {
        this.disabled = false;
        this.isLoadedPromise = null;
        this.window = windowObject;
        if (typeof this.window === 'undefined') {
            this.disabled = true;
        }
        else {
            this.setupSnippet();
        }
    }
    /**
     * Load analytics
     *
     * Analytics might never load on browsers with Ad Blockers.
     * To prevent returing a promise that never resolves, this method will resolve after a timeout.
     *
     * Hence there are 3 scenarios:
     *  1. (Happy path) - Segment has finished loading in time, yay!
     *  2. (Ad Blockers) - Segment will never load
     *     ...this method will resolve after a timeout
     *  3. (Slow Connexions) - 6 seconds have passed and this method has resolved, but Segment is still loading
     *     ... this is not a problem
     *     ... future events will be put in the queue until Loading is truly finished
     *     ... 6 seconds is what it takes chrome to load the analytics on a Slow 3G connexion.
     *
     * @returns A promise that resolve when analytics are loaded, to a max of wait time 6 seconds.
     */
    load(segmentWriteKey) {
        if (this.disabled) {
            return Promise.resolve();
        }
        this.isLoadedPromise =
            this.isLoadedPromise ||
                new Promise((resolve) => {
                    this.window.analytics.load(segmentWriteKey);
                    this.window.analytics.ready(() => {
                        resolve();
                    });
                });
        const timeoutPromise = new Promise((resolve) => {
            setTimeout(resolve, 6000);
        });
        return Promise.race([this.isLoadedPromise, timeoutPromise]);
    }
    /**
     * This method is based on the Segment snippet (and converted to Typescript).
     * It asynchronously loads Segment, and creates a queue used until Segment is ready.
     */
    setupSnippet() {
        // Create a queue, but don't obliterate an existing one!
        const analytics = (this.window.analytics = this.window.analytics || []);
        /**
         * Return early if
         * - the analytics snippet is already on the page
         * - the analytics snippet was already invoked
         */
        if (analytics.initialize || analytics.invoked) {
            return;
        }
        const segmentDomain = this.window && this.window.location.host.match(/(-dev|-demo|-partner|local).landr.com$/g)
            ? 'cdn2-segment-dev.landr.com'
            : 'cdn2-segment.landr.com';
        // Invoked flag, to make sure the snippet is never invoked twice.
        analytics.invoked = true;
        // A list of the methods in Segment's Analytics.js to stub.
        analytics.methods = [
            'trackSubmit',
            'trackClick',
            'trackLink',
            'trackForm',
            'pageview',
            'identify',
            'reset',
            'group',
            'track',
            'ready',
            'alias',
            'debug',
            'page',
            'once',
            'off',
            'on',
            'addSourceMiddleware',
            'addIntegrationMiddleware',
            'setAnonymousId',
            'addDestinationMiddleware',
        ];
        // Define a factory to create stubs. These are placeholders
        // for methods in Analytics.js so that you never have to wait
        // for it to load to actually record data. The `method` is
        // stored as the first argument, so we can replay the data.
        analytics.factory = (method) => {
            return function () {
                // eslint-disable-next-line prefer-rest-params
                const args = Array.prototype.slice.call(arguments);
                args.unshift(method);
                analytics.push(args);
                return analytics;
            };
        };
        // For each of our methods, generate a queueing stub.
        for (const key of analytics.methods) {
            analytics[key] = analytics.factory(key);
        }
        // Define a method to load Analytics.js from our CDN,
        // and that will be sure to only ever load it once.
        analytics.load = function (key, options) {
            // Create an async script element based on your key.
            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.async = true;
            script.defer = true;
            script.src = `https://${segmentDomain}/snippet.js/v1/${key}/snippet.min.js`;
            // Insert our script next to the first script element.
            const first = document.getElementsByTagName('script')[0];
            if (first && first.parentNode) {
                first.parentNode.insertBefore(script, first);
            }
            analytics._loadOptions = options;
            analytics._cdn = `https://${segmentDomain}`;
        };
        // Add a version to keep track of what's in the wild.
        analytics.SNIPPET_VERSION = '4.1.0';
        this.setupSegmentContextMiddleware();
    }
    setupSegmentContextMiddleware() {
        const uaResult = new UAParser().getResult();
        // eslint-disable-next-line max-len
        // See https://github.com/amplitude/Amplitude-JavaScript/blob/4aaa26f45919c5587bcc3fb21510ca8afbc41e4b/src/amplitude-client.js#L1173-L1175
        const additionalContext = {
            os: {
                name: uaResult.browser.name,
                version: uaResult.browser.version,
            },
            device: {
                model: uaResult.os.name,
            },
        };
        this.window.analytics.addSourceMiddleware(({ payload, next }) => {
            payload.obj.context = Object.assign(Object.assign({}, payload.context), additionalContext);
            next(payload);
        });
    }
}

export { SegmentLoader };
